/**
 * @author    Nikko Renolds
 * @package   Trap Tycoon
 * @link      https://github.com/Ni1kko/Trap-Tycoon
 * @copyright (c) Fallox Developments 2026.
 * @license   MIT
 * @description Enhanced Player Profile with Prestige, Collectibles, Combat Analytics, and Asset Summary.
 */

import React, { useEffect, useMemo, useState } from "react";
import {
  User,
  Zap,
  DollarSign,
  Medal,
  Skull,
  Star,
  Swords,
  Target,
  Shield,
  Users,
  TrendingUp,
  Activity,
  Flame,
  Siren,
  MessageSquare,
  UserPlus,
  UserMinus,
  Clock,
  Crown,
} from "lucide-react";
import { callBackend } from "../utils/api";
import { useNotify } from "./Notification";
import { log } from "../utils/logger";

// Helper component for visual performance bars
const StatBar = ({ label, value, color, max = 100 }) => (
  <div className="space-y-1">
    <div className="flex justify-between text-[10px] uppercase font-bold text-zinc-500">
      <span>{label}</span>
      <span>{value}%</span>
    </div>
    <div className="h-1 w-full bg-zinc-900 border border-zinc-800">
      <div
        className={`h-full ${color} transition-all duration-1000`}
        style={{ width: `${Math.min(value, 100)}%` }}
      />
    </div>
  </div>
);

const OgBadge = () => (
  <span
    className="group relative inline-flex shrink-0 cursor-help items-center rounded-sm outline-none"
    aria-label="Day 1 OG"
    title="Day 1 OG"
    tabIndex={0}
  >
    <span className="absolute -inset-1 rounded-md bg-amber-400/30 opacity-40 blur-sm transition-opacity duration-300 group-hover:opacity-100 group-focus:opacity-100 motion-reduce:transition-none" />
    <span
      aria-hidden="true"
      className="relative inline-flex items-center overflow-hidden rounded-sm border border-amber-300/80 bg-gradient-to-r from-amber-950 via-yellow-700 to-amber-950 px-2 py-1 text-[10px] font-black tracking-[0.18em] text-amber-100 shadow-[0_0_12px_rgba(251,191,36,0.35)] transition-all duration-300 group-hover:-translate-y-0.5 group-hover:scale-105 group-hover:shadow-[0_0_20px_rgba(251,191,36,0.7)] group-focus:-translate-y-0.5 group-focus:scale-105 motion-reduce:transform-none motion-reduce:transition-none"
    >
      <Crown
        size={12}
        className="mr-1 fill-amber-300 text-yellow-100 transition-transform duration-300 group-hover:-rotate-6 group-hover:scale-110 group-focus:-rotate-6 group-focus:scale-110 motion-reduce:transform-none motion-reduce:transition-none"
      />
      <span className="max-w-0 overflow-hidden whitespace-nowrap opacity-0 transition-all duration-300 group-hover:max-w-[3.75rem] group-hover:opacity-100 group-focus:max-w-[3.75rem] group-focus:opacity-100 motion-reduce:transition-none">
        DAY 1&nbsp;
      </span>
      <span>OG</span>
      <span className="absolute inset-y-0 -left-6 w-3 -skew-x-12 bg-white/40 blur-sm transition-transform duration-700 group-hover:translate-x-24 group-focus:translate-x-24 motion-reduce:hidden" />
    </span>
  </span>
);

export const stackCollectibles = (inventory = []) => {
  const stacks = new Map();

  inventory.forEach((item) => {
    if (item?.type !== "collectible" || !item.name) return;

    const quantity = Number(item.quantity);
    if (!Number.isFinite(quantity) || quantity <= 0) return;

    const name = item.name.trim();
    if (!name) return;

    const existing = stacks.get(name);
    stacks.set(name, {
      ...item,
      name,
      quantity: (existing?.quantity || 0) + quantity,
    });
  });

  return Array.from(stacks.values());
};

export default function PlayerProfile({
  user,
  targetUser,
  onAttack,
  onPostBounty,
  onOpenChat,
}) {
  const { addNotification } = useNotify();
  const displayUser = targetUser || user;
  const displayUserId = displayUser?.id;
  const isMe = !targetUser || (user && targetUser.id === user.id);
  const collectibles = useMemo(
    () => stackCollectibles(displayUser?.inventory),
    [displayUser?.inventory],
  );
  const [bountyAmount, setBountyAmount] = useState(0);
  const [bountyReason, setBountyReason] = useState("");
  const [maxLevel, setMaxLevel] = useState(999); // Default high value to avoid premature prestige button
  const [friendStatus, setFriendStatus] = useState("none");

  useEffect(() => {
    const fetchConstants = async () => {
      try {
        const data = await callBackend("/system/constants");
        if (data?.ENGINE?.MAX_LEVEL) {
          setMaxLevel(data.ENGINE.MAX_LEVEL); // Sync with backend ENGINE_MAX_LEVEL
        }
      } catch (err) {
        log("PLAYER_PROFILE", "Failed to fetch max level", err);
      }
    };
    fetchConstants();
  }, []);

  useEffect(() => {
    const checkStatus = async () => {
      if (!isMe && displayUser?.id) {
        try {
          const data = await callBackend(`/social/status/${displayUser.id}`);
          setFriendStatus(data.status);
        } catch (err) {
          log("PLAYER_PROFILE", "Failed to check status", err);
        }
      }
    };
    checkStatus();
  }, [displayUser.id, isMe]);

  const isOnline =
    new Date(displayUser.last_request_at) > new Date(Date.now() - 5 * 60000);

  const handleBribe = async () => {
    if (
      !window.confirm(
        "Bribe the police chief to reduce your Heat Level? This will cost cash.",
      )
    )
      return;
    try {
      const result = await callBackend("/pay-bribe", { method: "POST" });
      addNotification(result.message, "success");
      window.location.reload();
    } catch (err) {
      addNotification(err.message, "error");
      log("PLAYER_PROFILE", "Failed to bribe police chief", err);
    }
  };

  useEffect(() => {
    if (!isMe || !displayUserId) return;

    callBackend("/prestige/check-veteran").catch((err) =>
      log("PLAYER_PROFILE", "Failed to check veteran coin", err),
    );
  }, [displayUserId, isMe]);

  // Handle removing friend/cancelling request
  const handleRemoveFriend = async () => {
    const confirmMsg =
      friendStatus === "friends"
        ? "Drop this member from your crew?"
        : "Cancel this pending invitation?";

    if (!window.confirm(confirmMsg)) return;

    try {
      await callBackend("/social/remove", {
        method: "POST",
        body: { targetId: displayUser.id },
      });
      setFriendStatus("none");
      addNotification("Relationship terminated.", "success");
    } catch (err) {
      addNotification(err.message, "error");
    }
  };

  // Update existing handleAddFriend to update state locally
  const handleAddFriend = async () => {
    try {
      const data = await callBackend("/social/request", {
        method: "POST",
        body: { targetId: displayUser.id },
      });
      addNotification(data.message, "success");
      setFriendStatus("sent"); // <--- Add this
    } catch (err) {
      addNotification(err.message, "error");
    }
  };

  const handleAcceptFriend = async (requesterId) => {
    try {
      const data = await callBackend("/social/accept", {
        method: "POST",
        body: { requesterId },
      });
      addNotification(data.message, "success");
      // Refresh the social view if necessary
    } catch (err) {
      addNotification(err.message, "error");
      log("PLAYER_PROFILE", "Failed to accept friend", err);
    }
  };

  const handlePrestige = async () => {
    if (
      !window.confirm(
        "WARNING: This will reset your Rank to 1. You will keep your items and cash, but lose your level progress. Proceed?",
      )
    )
      return;

    try {
      const data = await callBackend("/prestige/claim", {
        method: "POST",
        body: { clientStats: user },
      });
      addNotification(data.message, "success");
      window.location.reload();
    } catch (err) {
      addNotification(err.message, "error");
      log("PLAYER_PROFILE", "Failed to claim prestige", err);
    }
  };

  const getMedalColor = (name) => {
    if (!name) return "text-zinc-400";
    if (name.includes("Level 6")) return "text-red-500";
    if (name.includes("Level 5")) return "text-pink-500";
    if (name.includes("Level 4")) return "text-purple-500";
    if (name.includes("Level 3")) return "text-blue-500";
    if (name.includes("Level 2")) return "text-green-500";
    return "text-zinc-400";
  };

  if (!displayUser) return null;

  // Logic for visual analytics
  const winRate =
    displayUser.fights_won + displayUser.fights_lost > 0
      ? Math.round(
          (displayUser.fights_won /
            (displayUser.fights_won + displayUser.fights_lost)) *
            100,
        )
      : 0;

  return (
    <div className="space-y-6 animate-in fade-in duration-500 font-mono">
      {/* HEADER SECTION */}
      <div className="bg-zinc-950 border-2 border-zinc-800 p-6 relative overflow-hidden">
        <div className="flex flex-col md:flex-row justify-between items-start relative z-10 gap-6">
          <div className="flex gap-6 items-center">
            <div className="w-24 h-24 bg-black border-2 border-zinc-800 flex items-center justify-center relative">
              {displayUser.discordUser?.avatar ? (
                <img
                  src={displayUser.discordUser.avatar}
                  alt="Profile"
                  className="w-full h-full object-cover"
                />
              ) : (
                <User size={48} className="text-zinc-600" />
              )}

              {displayUser.is_contributor && (
                <div
                  className="absolute -top-2 -right-2 bg-blue-600 text-white p-1 rounded-full shadow-[0_0_15px_rgba(37,99,235,0.5)] border border-blue-400"
                  title="Contributor"
                >
                  <Star size={12} fill="white" />
                </div>
              )}

              {/* Online/Offline Pulsing Dot */}
              <div
                className={`absolute -bottom-1 -left-1 w-4 h-4 rounded-full border-2 border-black shadow-lg ${
                  isOnline
                    ? "bg-green-500 animate-pulse shadow-[0_0_8px_rgba(34,197,94,0.5)]"
                    : "bg-zinc-800"
                }`}
                title={
                  isOnline
                    ? "Online Now"
                    : `Last active: ${new Date(
                        displayUser.last_request_at,
                      ).toLocaleString()}`
                }
              />
            </div>

            <div>
              <div className="flex flex-wrap items-center gap-3">
                <h2 className="text-3xl font-black text-white uppercase italic flex flex-wrap items-center gap-2">
                  {displayUser.username}

                  {Number(displayUser.is_og) === 1 && <OgBadge />}

                  {displayUser.role && displayUser.role !== "user" && (
                    <span className="text-xs bg-red-600 text-black px-2 py-1 rounded-sm not-italic font-bold">
                      {displayUser.role.toUpperCase()}
                    </span>
                  )}

                  {/*<span
                    className={`text-[10px] not-italic font-bold px-2 py-0.5 rounded ${
                      isOnline
                        ? "text-green-500 bg-green-500/10"
                        : "text-zinc-500 bg-zinc-900"
                    }`}
                  >
                    {isOnline ? "ACTIVE ON THE BLOCK" : "OFFLINE"}
                  </span>*/}
                </h2>

                {/* Medals Display */}
                {collectibles.length > 0 && (
                  <div className="flex max-w-full flex-wrap items-center gap-1.5 rounded-md border border-zinc-800 bg-zinc-900/50 px-2 py-1.5">
                    {collectibles.map((item) => (
                      <div
                        key={item.name}
                        className="group relative inline-flex cursor-help items-center gap-1 rounded-sm border border-zinc-700 bg-black/70 px-1.5 py-1 outline-none transition-colors hover:border-zinc-500 focus:border-zinc-500"
                        title={`${item.name} x${item.quantity}`}
                        aria-label={`${item.name}: ${item.quantity} owned`}
                        data-badge-stack={item.name}
                        tabIndex={0}
                      >
                        <Medal
                          size={18}
                          className={`${getMedalColor(
                            item.name,
                          )} transition-transform group-hover:scale-110 group-focus:scale-110 motion-reduce:transform-none motion-reduce:transition-none`}
                        />
                        <span className="rounded-sm border border-zinc-700 bg-zinc-900 px-1 text-[9px] font-black not-italic text-amber-300">
                          ×{item.quantity}
                        </span>
                        <div className="absolute left-1/2 top-full z-50 mt-2 hidden w-max max-w-56 -translate-x-1/2 border border-zinc-700 bg-black px-2 py-1.5 text-center text-[10px] font-bold uppercase not-italic text-white shadow-xl group-hover:block group-focus:block">
                          <span className="block">{item.name}</span>
                          <span className="block text-[8px] text-amber-400">
                            Owned: {item.quantity}
                          </span>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>

              <div className="text-sm text-zinc-500 mt-2 space-y-1">
                <p>ID: #{displayUser.id?.toString().padStart(6, "0")}</p>
                <p>
                  Joined:{" "}
                  {new Date(displayUser.created_at).toLocaleDateString()}
                </p>
                <p className="text-[10px] uppercase font-bold text-zinc-600">
                  Last Seen:{" "}
                  {displayUser.last_request_at
                    ? new Date(displayUser.last_request_at).toLocaleString()
                    : "NEVER"}
                </p>
                {displayUser.alliance_name && (
                  <p className="text-blue-400 font-bold uppercase">
                    Member of [{displayUser.alliance_tag}]{" "}
                    {displayUser.alliance_name}
                  </p>
                )}
              </div>
            </div>
          </div>

          <div className="text-left md:text-right flex flex-col gap-2 w-full md:w-auto">
            <p className="text-xs text-zinc-500 uppercase font-bold">
              Current Rank
            </p>
            <p className="text-4xl font-black text-white leading-none">
              LEVEL {displayUser.level}
            </p>
            {isMe && displayUser.level >= maxLevel && (
              <button
                onClick={handlePrestige}
                className="mt-2 bg-yellow-600 hover:bg-yellow-500 text-black font-black px-4 py-2 text-xs uppercase flex items-center gap-2 transition-all shadow-[0_0_15px_rgba(234,179,8,0.4)] animate-pulse ml-auto"
              >
                <Medal size={14} /> Get Service Medal
              </button>
            )}
            {!isMe && (
              <div className="flex flex-col gap-2 mt-2">
                <button
                  onClick={() => onOpenChat(displayUser)}
                  className="bg-blue-600 hover:bg-blue-500 text-white font-black py-2 px-6 uppercase text-xs flex items-center justify-center gap-2 transition-all shadow-[0_0_10px_rgba(37,99,235,0.2)]"
                >
                  <MessageSquare size={14} /> MESSAGE
                </button>

                {/* DYNAMIC FRIEND BUTTON */}
                {friendStatus === "none" && (
                  <button
                    onClick={handleAddFriend}
                    className="bg-zinc-800 hover:bg-zinc-700 text-white font-black py-2 px-6 uppercase text-xs flex items-center justify-center gap-2 transition-all"
                  >
                    <UserPlus size={14} /> ADD
                  </button>
                )}

                {friendStatus === "sent" && (
                  <button
                    onClick={handleRemoveFriend}
                    className="bg-zinc-900 border border-yellow-600/50 text-yellow-500 font-black py-2 px-6 uppercase text-xs flex items-center justify-center gap-2 transition-all"
                  >
                    <Clock size={14} className="animate-pulse" /> Pending
                    Request
                  </button>
                )}

                {friendStatus === "received" && (
                  <button
                    onClick={() => handleAcceptFriend(displayUser.id)}
                    className="bg-green-600 hover:bg-green-500 text-black font-black py-2 px-6 uppercase text-xs flex items-center justify-center gap-2 transition-all"
                  >
                    <UserPlus size={14} /> ACCEPT REQUEST
                  </button>
                )}

                {friendStatus === "friends" && (
                  <button
                    onClick={handleRemoveFriend}
                    className="bg-zinc-900 border border-red-900 text-red-600 hover:bg-red-900 hover:text-white font-black py-2 px-6 uppercase text-xs flex items-center justify-center gap-2 transition-all group"
                  >
                    <UserMinus size={14} />
                    <span className="group-hover:hidden">FRIENDS</span>
                    <span className="hidden group-hover:inline">UNFRIEND</span>
                  </button>
                )}
              </div>
            )}
          </div>
        </div>
      </div>

      {/* HEAT & STATUS SECTION */}
      <div className="bg-gradient-to-r from-zinc-900 to-black border border-zinc-800 p-4 flex justify-between items-center">
        <div>
          <h3 className="text-xs font-black text-orange-500 uppercase flex items-center gap-2">
            <Flame size={14} /> Heat Level
          </h3>
          <p className="text-[10px] text-zinc-500">
            Police Attention:{" "}
            <span className="text-white">
              {displayUser.heat_level || 0}/100
            </span>
          </p>
        </div>
        <div className="flex gap-1">
          {[...Array(5)].map((_, i) => (
            <div
              key={i}
              className={`w-8 h-2 rounded-sm ${
                i < Math.ceil((displayUser.heat_level || 0) / 20)
                  ? "bg-orange-600 shadow-[0_0_10px_orange]"
                  : "bg-zinc-800"
              }`}
            />
          ))}
        </div>
        {isMe && displayUser.heat_level > 0 && (
          <button
            onClick={handleBribe}
            className="ml-4 bg-zinc-800 hover:bg-orange-900 text-orange-500 border border-orange-900/50 px-3 py-1 text-[10px] font-black uppercase flex items-center gap-2 transition-all"
          >
            <Siren size={12} /> Pay Bribe
          </button>
        )}
      </div>

      {/* VISUAL ANALYTICS SECTION */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 bg-zinc-950 border border-zinc-800 p-4">
        <div className="space-y-4">
          <h3 className="text-xs font-black text-zinc-400 uppercase flex items-center gap-2">
            <Activity size={14} /> Combat Record
          </h3>
          <StatBar
            label="Win/Loss Efficiency"
            value={winRate}
            color="bg-red-600"
          />
          <StatBar
            label="Reputation Rank"
            value={Math.min(displayUser.level * 2, 100)}
            color="bg-blue-600"
          />
        </div>
        <div className="space-y-4">
          <h3 className="text-xs font-black text-zinc-400 uppercase flex items-center gap-2">
            <TrendingUp size={14} /> Financial Standing
          </h3>
          <StatBar
            label="Capital Liquidity"
            value={Math.round(
              (displayUser.cash / (displayUser.cash_earned || 1)) * 100,
            )}
            color="bg-green-600"
          />
        </div>
      </div>

      {/* STATS GRID */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <div className="bg-black border border-zinc-800 p-4">
          <p className="text-[10px] text-zinc-500 uppercase font-bold flex items-center gap-1">
            <DollarSign size={12} className="text-green-500" /> Liquid Cash
          </p>
          <p className="text-lg font-black text-green-400">
            ${(displayUser.cash || 0).toLocaleString()}
          </p>
        </div>

        <div className="bg-black border border-zinc-800 p-4">
          <p className="text-[10px] text-zinc-500 uppercase font-bold flex items-center gap-1">
            <Target size={12} className="text-red-500" /> Bounty Earnings
          </p>
          <p className="text-lg font-black text-white">
            ${(displayUser.bounty_earnings || 0).toLocaleString()}
          </p>
        </div>

        <div className="bg-black border border-zinc-800 p-4">
          <p className="text-[10px] text-zinc-500 uppercase font-bold flex items-center gap-1">
            <Zap size={12} className="text-yellow-500" /> Street Power
          </p>
          <p className="text-lg font-black text-white">
            {(displayUser.street_power || 0).toLocaleString()}
          </p>
        </div>

        <div className="bg-black border border-zinc-800 p-4">
          <p className="text-[10px] text-zinc-500 uppercase font-bold flex items-center gap-1">
            <Skull size={12} className="text-red-500" /> Fights Won
          </p>
          <p className="text-lg font-black text-white">
            {(displayUser.fights_won || 0).toLocaleString()}
          </p>
        </div>
      </div>

      {/* ASSET SUMMARY */}
      <div className="bg-black border border-zinc-800 p-6">
        <h3 className="text-sm font-bold text-zinc-400 uppercase mb-4 border-b border-zinc-800 pb-2 flex items-center gap-2">
          <Shield size={16} /> Asset Inventory
        </h3>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
          <div className="text-center">
            <Users size={20} className="mx-auto mb-2 text-zinc-500" />
            <p className="text-[10px] text-zinc-500 uppercase">Mercenaries</p>
            <p className="font-black text-white">
              {displayUser.inventorySummary?.mercenaries || 0}
            </p>
          </div>
          <div className="text-center">
            <Skull size={20} className="mx-auto mb-2 text-zinc-500" />
            <p className="text-[10px] text-zinc-500 uppercase">Attack Dogs</p>
            <p className="font-black text-white">
              {displayUser.inventorySummary?.dogs || 0}
            </p>
          </div>
          <div className="text-center">
            <Swords size={20} className="mx-auto mb-2 text-zinc-500" />
            <p className="text-[10px] text-zinc-500 uppercase">Weaponry</p>
            <p className="font-black text-white">
              {displayUser.inventorySummary?.weapons || 0}
            </p>
          </div>
          <div className="text-center">
            <Shield size={20} className="mx-auto mb-2 text-zinc-500" />
            <p className="text-[10px] text-zinc-500 uppercase">Protection</p>
            <p className="font-black text-white">
              {displayUser.inventorySummary?.armor || 0}
            </p>
          </div>
        </div>
      </div>

      {/* LIFETIME CASUALTIES & ATTRITION */}
      <div className="bg-zinc-950/50 border border-zinc-800 p-4 grid grid-cols-3 gap-4 text-center italic">
        <div>
          <p className="text-[9px] text-zinc-600 uppercase">Thugs Lost</p>
          <p className="text-xs font-bold text-red-900">
            {displayUser.thug_losses || 0}
          </p>
        </div>
        <div>
          <p className="text-[9px] text-zinc-600 uppercase">Girls Lost</p>
          <p className="text-xs font-bold text-red-900">
            {displayUser.girls_lost || 0}
          </p>
        </div>
        <div>
          <p className="text-[9px] text-zinc-600 uppercase">Runners Busted</p>
          <p className="text-xs font-bold text-red-900">
            {displayUser.runners_busted || 0}
          </p>
        </div>
      </div>

      {/* INTERACTION ZONE */}
      {!isMe && (
        <div className="pt-6 border-t border-zinc-800">
          <div className="space-y-4">
            <button
              onClick={() => onAttack(displayUser.id)}
              className="w-full flex items-center justify-center gap-3 bg-red-700 hover:bg-red-500 text-black font-black py-4 transition-all uppercase italic text-xl"
            >
              <Swords size={24} /> ROLL ON THIS PLAYER
            </button>

            <div className="border border-zinc-800 p-6 bg-black">
              <h3 className="text-sm font-bold text-zinc-400 uppercase mb-4 flex items-center gap-2">
                <Target size={16} className="text-red-500" />
                {displayUser.total_bounty > 0
                  ? "Active Bounty Detected"
                  : "Issue a Contract"}
              </h3>

              {displayUser.total_bounty > 0 && (
                <div className="bg-red-900/10 border border-red-900 p-3 mb-4 text-center">
                  <p className="text-[10px] text-zinc-500 uppercase font-black">
                    Current Reward
                  </p>
                  <p className="text-2xl font-black text-red-600 animate-pulse">
                    ${displayUser.total_bounty.toLocaleString()}
                  </p>
                </div>
              )}

              <div className="flex flex-col gap-2">
                <div className="relative flex-1">
                  <DollarSign
                    className="absolute left-3 top-1/2 -translate-y-1/2 text-zinc-500"
                    size={16}
                  />
                  <input
                    type="number"
                    value={bountyAmount}
                    onChange={(e) => setBountyAmount(e.target.value)}
                    className="w-full bg-zinc-900 border border-zinc-700 p-2 pl-9 text-white font-mono focus:outline-none focus:border-red-600"
                  />
                </div>
                <input
                  placeholder="REASON (OPTIONAL)"
                  className="w-full bg-zinc-900 border border-zinc-700 p-2 text-[10px] text-white mt-2 mb-2 uppercase"
                  onChange={(e) => setBountyReason(e.target.value)}
                  value={bountyReason}
                />
                <button
                  onClick={() =>
                    onPostBounty(displayUser.id, bountyAmount, bountyReason)
                  }
                  className="bg-zinc-800 hover:bg-white hover:text-black text-white px-6 font-black uppercase text-xs py-3 transition-colors italic"
                >
                  {displayUser.total_bounty > 0
                    ? "Increase Bounty Reward"
                    : "Post Bounty"}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
